import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  FileText,
  DollarSign,
  Megaphone,
  Bell,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Users,
  Shield,
  CreditCard,
  Wallet,
  UserCheck,
  Calendar,
  CheckCircle,
  Building2,
  Bot,
  Activity,
  ArrowLeftRight,
  BookUser,
  Flame,
  Coins,
  Plus,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { announcementsAPI } from "../../services/api";
import { usePermissions, ROUTE_PERMISSION_MAP } from "../../utils/permissionUtils";

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false);
  const { user, logout, isLoggingOut, getDisplayName } = useAuth();
  const { hasPermission, isSubAccount, getPermissionForRoute } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  // Show top-bar logout button on all pages
  // Removed dashboard-only hiding; logout button is shown on all pages

  // Safe display name function to prevent object rendering errors
  const safeGetDisplayName = () => {
    try {
      const name = getDisplayName();
      return typeof name === "string" ? name : "User";
    } catch (error) {
      console.error("Error getting display name in Layout:", error);
      return "User";
    }
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = async () => {
    setShowLogoutModal(false);
    await logout();
    // Use replace: true to prevent old routes from staying in browser history
    navigate("/login", { replace: true });
  };

  // Get settings path based on role
  const getSettingsPath = () => {
    const settingsPathMap = {
      facility_agent: "/facility-agent/settings",
      primary_subscriber: "/primary-subscriber/settings",
      issuer: "/issuer/settings",
      custodian: "/custodian/settings",
      super_admin: "/admin/settings",
      investor: "/investor/settings",
      trustee: "/trustee/settings",
    };
    return settingsPathMap[user?.role] || `/${user?.role}/settings`;
  };

  // Handle navigation click - redirect to settings if password change is required
  const handleNavigationClick = (e, href) => {
    // Allow navigation to settings page even if mustChangePassword is true
    if (user?.mustChangePassword && !href.includes("/settings")) {
      e.preventDefault();
      navigate(getSettingsPath(), { replace: true });
      setSidebarOpen(false);
    } else {
      setSidebarOpen(false);
    }
  };

  // Quick logout removed for consistency and simplicity

  // Define navigation items based on user role
  const getNavigationItems = () => {
    switch (user?.role) {
      case "issuer":
        return [
          { name: "Dashboard", href: "/issuer", icon: Home },
          {
            name: "Create Bond Application",
            href: "/issuer/bonds/create",
            icon: FileText,
          },
          { name: "Bond Management", href: "/issuer/bonds", icon: FileText },
          // Whitelist Management hidden per request
          // { name: "Whitelist Management", href: "/issuer/whitelist-management", icon: UserCheck },
          // Token Transfers hidden per request
          // { name: "Token Transfers", href: "/issuer/token-transfers", icon: Shield },
          { name: "Documents", href: "/issuer/documents", icon: FileText },
          {
            name: "Announcements",
            href: "/issuer/announcements",
            icon: Megaphone,
          },
          {
            name: "Sub Account Management",
            href: "/issuer/sub-accounts",
            icon: Users,
          },
          { name: "Role Management", href: "/issuer/roles", icon: Shield },
          { name: "Audit Logs", href: "/issuer/audit-logs", icon: Shield },
          { name: "Settings", href: "/issuer/settings", icon: Settings },
        ];

      case "facility_agent":
        return [
          { name: "Dashboard", href: "/facility-agent", icon: Home },
          {
            name: "Subscription Management",
            href: "/facility-agent/subscription",
            icon: Users,
          },
          {
            name: "Create Sub Agreement",
            href: "/facility-agent/create-sub-agreement",
            icon: Plus,
          },
          {
            name: "Sub Agreement Management",
            href: "/facility-agent/sub-agreement-management",
            icon: FileText,
          },
          {
            name: "Bond Applications",
            href: "/facility-agent/applications",
            icon: FileText,
          },
          {
            name: "Bond Holdings",
            href: "/facility-agent/bond-holdings",
            icon: CreditCard,
          },
          {
            name: "Issuance Payment Management",
            href: "/facility-agent/issuance-payment-management",
            icon: Flame,
          },
          {
            name: "Coupon Management",
            href: "/facility-agent/coupon-profit-distribution",
            icon: DollarSign,
          },
          // Hidden per request: Redemption Management
          // {
          //   name: "Redemption Management",
          //   href: "/facility-agent/redemption",
          //   icon: Calendar,
          // },
          {
            name: "Maturity Management",
            href: "/facility-agent/maturity-management",
            icon: Calendar,
          },
          {
            name: "Bond Holder",
            href: "/facility-agent/token-holder-register",
            icon: BookUser,
          },
          { name: "Documents", href: "/facility-agent/documents", icon: FileText },
          {
            name: "Sub Account Management",
            href: "/facility-agent/sub-accounts",
            icon: Users,
          },
          {
            name: "Role Management",
            href: "/facility-agent/roles",
            icon: Shield,
          },
          { name: "Audit Log", href: "/facility-agent/reports", icon: Shield },
          {
            name: "Settings",
            href: "/facility-agent/settings",
            icon: Settings,
          },
        ];

      case "primary_subscriber":
        return [
          { name: "Dashboard", href: "/primary-subscriber", icon: Home },
          {
            name: "Create LOC",
            href: "/primary-subscriber/create-loc",
            icon: FileText,
          },
          {
            name: "LOC Management",
            href: "/primary-subscriber/loc-management",
            icon: FileText,
          },
          {
            name: "Submit Sub Agreement",
            href: "/primary-subscriber/submit-sub-agreement",
            icon: FileText,
          },
          {
            name: "Receive Bond Token",
            href: "/primary-subscriber/bond-token-requests",
            icon: FileText,
          },
          {
            name: "Bond Token Allocation",
            href: "/primary-subscriber/bond-token-allocation",
            icon: FileText,
          },
          {
            name: "Bond Holdings",
            href: "/primary-subscriber/bond-holdings",
            icon: Coins,
          },
          {
            name: "Bond Holder",
            href: "/primary-subscriber/token-holder-register",
            icon: BookUser,
          },
          { name: "Documents", href: "/primary-subscriber/documents", icon: FileText },
          {
            name: "Sub Account Management",
            href: "/primary-subscriber/sub-accounts",
            icon: Users,
          },
          {
            name: "Role Management",
            href: "/primary-subscriber/roles",
            icon: Shield,
          },
          {
            name: "Settings",
            href: "/primary-subscriber/settings",
            icon: Settings,
          },
        ];

      case "custodian":
        return [
          { name: "Dashboard", href: "/custodian", icon: Home },
          {
            name: "Sub Investors",
            href: "/custodian/sub-investors",
            icon: UserCheck,
          },
          {
            name: "Sub Investor LOC",
            href: "/custodian/loc-requests",
            icon: FileText,
          },
          // Hidden: Subscription Allocation and Allocation Preview
          // {
          //   name: "Subscription Allocation",
          //   href: "/custodian/subscription-allocation",
          //   icon: Users,
          // },
          // {
          //   name: "Allocation Preview",
          //   href: "/custodian/allocation-preview",
          //   icon: Users,
          // },

          {
            name: "Receive Bond Token",
            href: "/investor/bond-token-requests",
            icon: CheckCircle,
          },
          {
            name: "Bond Token Allocation",
            href: "/custodian/bond-token-allocation",
            icon: FileText,
          },
          {
            name: "Bond Holdings",
            href: "/custodian/bond-holdings",
            icon: CreditCard,
          },

          // { name: 'Payment Processing', href: '/custodian/payments', icon: DollarSign },

          // Hidden: legacy Coupon Distribution Requests entry (replaced by unified Coupon Profit Distribution)
          // {
          //   name: "Coupon Distribution Requests",
          //   href: "/custodian/coupon-distribution-requests",
          //   icon: DollarSign,
          // },
          {
            name: "Coupon Management",
            href: "/custodian/coupon-profit-distribution",
            icon: DollarSign,
          },
          {
            name: "Maturity Management",
            href: "/custodian/maturity-management",
            icon: Calendar,
          },
          {
            name: "Wallet Management",
            href: "/custodian/wallets",
            icon: Wallet,
          },
          // {
          //   name: "Client Transfers",
          //   href: "/custodian/client-transfers",
          //   icon: ArrowLeftRight,
          // },
          // {
          //   name: "Token Distribution",
          //   href: "/custodian/tokens",
          //   icon: FileText,
          // },
          { name: "Documents", href: "/custodian/documents", icon: FileText },
          {
            name: "Announcements",
            href: "/custodian/announcements",
            icon: Megaphone,
          },
          { name: "Role Management", href: "/custodian/roles", icon: Shield },
          {
            name: "Sub Account Management",
            href: "/custodian/sub-accounts",
            icon: Users,
          },
          { name: "Reports", href: "/custodian/reports", icon: BarChart3 },
          { name: "Settings", href: "/custodian/settings", icon: Settings },
        ];

      case "super_admin":
        return [
          { name: "Dashboard", href: "/admin", icon: Home },
          { name: "User Management", href: "/admin/users", icon: Users },
          {
            name: "Sub Account Management",
            href: "/admin/sub-accounts",
            icon: Users,
          },
          { name: "Role Management", href: "/admin/roles", icon: Shield },
          { name: "Documents", href: "/admin/documents", icon: FileText },
          { name: "Settings", href: "/admin/settings", icon: Settings },
        ];

      case "investor":
        // Check if user is a sub-investor (created by a custodian)
        const isSubInvestor = !!user?.createdBy;
        // Check if user is a sub-account of a sub-investor
        const isSubAccountOfSubInvestor =
          user?.isSubAccount && !!user?.parentAccountCreatedBy;
        const shouldHideBondTokenRequests =
          isSubInvestor || isSubAccountOfSubInvestor;

        const investorNavItems = [
          { name: "Dashboard", href: "/investor", icon: Home },
          {
            name: "Submit LOC",
            href: "/investor/subscription-requests",
            icon: FileText,
          },
          // Only show Receive Bond Token for direct investors and their sub-accounts (not sub-investors or sub-accounts of sub-investors)
          ...(shouldHideBondTokenRequests
            ? []
            : [
              {
                name: "Receive Bond Token",
                href: "/investor/bond-token-requests",
                icon: CheckCircle,
              },
            ]),
          {
            name: "Bond Holdings",
            href: "/investor/holdings",
            icon: BarChart3,
          },
          // Secondary Transfers should be visible for all investor accounts (direct + sub)
          // {
          //   name: "Secondary Transfers",
          //   href: "/investor/secondary-transfers",
          //   icon: ArrowLeftRight,
          //   alwaysShow: true,
          // },
          // Hidden: legacy Coupon Distribution Requests entry (replaced by unified Payout Management)
          // {
          //   name: "Coupon Distribution Requests",
          //   href: "/investor/coupon-distribution-requests",
          //   icon: DollarSign,
          // },
          {
            name: "Coupon Management",
            href: "/investor/payouts",
            icon: DollarSign,
          },
          {
            name: "Maturity Management",
            href: "/investor/maturity-management",
            icon: Calendar,
          },
          // Only show Wallet Management for direct investors and their sub-accounts (not sub-investors or sub-accounts of sub-investors)
          ...(shouldHideBondTokenRequests
            ? []
            : [
              {
                name: "Wallet Management",
                href: "/investor/wallet",
                icon: Wallet,
              },
            ]),
          {
            name: "Sub Account Management",
            href: "/investor/sub-accounts",
            icon: Users,
          },
          { name: "Role Management", href: "/investor/roles", icon: Shield },
          {
            name: "Announcements",
            href: "/investor/announcements",
            icon: Megaphone,
          },
          { name: "Documents", href: "/investor/documents", icon: FileText },
          { name: "Report", href: "/investor/reports", icon: FileText },
          { name: "Settings", href: "/investor/settings", icon: Settings },
        ];

        return investorNavItems;

      case "trustee":
        return [
          { name: "Dashboard", href: "/trustee", icon: Home },
          { name: "Assigned Bonds", href: "/trustee/bonds", icon: Building2 },
          { name: "Bond Holders", href: "/trustee/holders", icon: Users },
          { name: "Documents", href: "/trustee/documents", icon: FileText },
          {
            name: "Announcements",
            href: "/trustee/announcements",
            icon: Megaphone,
          },
          { name: "Role Management", href: "/trustee/roles", icon: Shield },
          { name: "Sub Account Management", href: "/trustee/subaccounts", icon: Users },
        ];

      default:
        return [
          { name: "Dashboard", href: "/", icon: Home },
          { name: "Settings", href: "/settings", icon: Settings },
        ];
    }
  };

  // Filter navigation items based on permissions for sub-accounts
  const filterNavigationItems = (items) => {
    if (!isSubAccount) {
      // Parent accounts have access to all navigation items
      return items;
    }

    return items.filter((item) => {
      // Dashboard is always accessible for all subaccounts
      if (item.name === 'Dashboard') {
        return true;
      }

      // Sub Account Management is always accessible
      if (item.href.includes('/sub-accounts') || item.href.includes('/subaccounts')) {
        return true;
      }

      // Role Management is always accessible
      if (item.href.includes('/roles')) {
        return true;
      }

      // Settings is always accessible
      if (item.href.includes('/settings')) {
        return true;
      }

      // Get the permission required for this route
      const permission = getPermissionForRoute(item.href);

      // If no permission required, allow access
      if (!permission) {
        return true;
      }

      // Check if user has the required permission
      return hasPermission(permission);
    });
  };

  const navigationItems = useMemo(() => {
    const items = getNavigationItems();
    return filterNavigationItems(items);
  }, [user, isSubAccount, hasPermission]);

  const isActive = (href) => {
    // Exact match for dashboard routes
    if (
      href === "/investor" ||
      href === "/custodian" ||
      href === "/issuer" ||
      href === "/facility-agent" ||
      href === "/primary-subscriber" ||
      href === "/admin" ||
      href === "/trustee"
    ) {
      return location.pathname === href;
    }

    // Special handling for issuer bond routes to prevent overlap
    if (
      href === "/issuer/bonds" &&
      location.pathname.startsWith("/issuer/bonds/create")
    ) {
      return false; // Don't highlight "Bond Management" when on "Create Bond" page
    }

    // Special handling for bond-related menus when viewing bond documents
    // e.g., /trustee/bonds/:bondId/documents should NOT highlight /trustee/bonds
    if (
      location.pathname.includes("/bonds/") &&
      location.pathname.endsWith("/documents") &&
      href.endsWith("/bonds")
    ) {
      return false; // Don't highlight "Bonds" menu when viewing bond documents
    }

    // Special handling for Documents menu when viewing bond documents
    // e.g., /trustee/bonds/:bondId/documents should highlight /trustee/documents
    if (
      href.endsWith("/documents") &&
      location.pathname.includes("/bonds/") &&
      location.pathname.endsWith("/documents")
    ) {
      const rolePrefix = href.split("/documents")[0];
      return location.pathname.startsWith(rolePrefix);
    }

    // For other routes, check if current path starts with the href
    return location.pathname.startsWith(href);
  };

  const getRoleDisplayName = () => {
    // Handle investor role - differentiate Direct Investor vs Sub Investor
    if (user?.role === "investor") {
      if (user?.isSubAccount) {
        // This is a subaccount - check if parent was created by custodian
        return user?.parentAccountCreatedBy ? "Sub Investor" : "Direct Investor";
      } else {
        // This is a main/admin account - check if created by custodian
        return user?.createdBy ? "Sub Investor" : "Direct Investor";
      }
    }

    const roleNames = {
      issuer: "Issuer",
      facility_agent: "Facility Agent",
      primary_subscriber: "Primary Subscriber",
      custodian: "Custodian",
      super_admin: "Super Admin",
      trustee: "Trustee",
    };
    return roleNames[user?.role] || "User";
  };

  const getCompanyName = () => {
    // Try different possible field names for company name
    return (
      user?.corporateInfo?.legalEntityName ||
      user?.corporateInfo?.companyName ||
      user?.corporateInfo?.businessName ||
      null
    );
  };

  return (
    <div className="flex h-screen bg-secondary-50">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-72 flex-shrink-0 shadow-2xl transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:flex lg:flex-col`}
        style={{
          background: "var(--color-primary)",
          minWidth: "18rem",
          maxWidth: "18rem",
        }}
      >
        <div
          className="flex items-center justify-between flex-shrink-0 px-6 py-4 border-b border-gray-600"
          style={{ background: "transparent" }}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <img
                src={`${process.env.PUBLIC_URL}/khazanah_sidebar.svg`}
                alt="Khazanah Logo"
                className="w-8 h-8"
              />
            </div>
            <div className="flex-1 min-w-0 ml-3">
              <h1 className="text-lg font-bold text-white">TBPP</h1>
              <div className="mt-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-white">
                    {getRoleDisplayName()}
                  </p>
                  {user?.isSubAccount ? (
                    <span className="px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full bg-blue-100 text-blue-700">
                      Subaccount
                    </span>
                  ) : (
                    <span className="px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full bg-blue-100 text-blue-700">
                      Admin
                    </span>
                  )}
                </div>
                {getCompanyName() && (
                  <p className="text-xs text-white/80 mt-0.5">
                    {getCompanyName()}
                  </p>
                )}
                <p
                  className="text-xs text-white/70 mt-0.5 mb-2 truncate"
                  title={typeof user?.email === "string" ? user.email : ""}
                >
                  {typeof user?.email === "string" ? user.email : ""}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="flex-shrink-0 text-white lg:hidden hover:text-primary-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="flex-1 px-4 mt-8 overflow-y-auto">
          <div className="space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${user?.mustChangePassword && !item.href.includes("/settings")
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                    }`}
                  style={{
                    backgroundColor: isActive(item.href)
                      ? "#ffffff"
                      : "transparent",
                    color: isActive(item.href)
                      ? "var(--color-primary)"
                      : "#ffffff",
                  }}
                  onMouseEnter={(e) => {
                    if (
                      !isActive(item.href) &&
                      !(
                        user?.mustChangePassword &&
                        !item.href.includes("/settings")
                      )
                    ) {
                      e.currentTarget.style.backgroundColor =
                        "rgba(255, 255, 255, 0.15)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive(item.href)) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  onClick={(e) => handleNavigationClick(e, item.href)}
                >
                  <Icon className="flex-shrink-0 w-5 h-5 mr-3" />
                  <span className="flex-1 whitespace-nowrap">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User info and logout */}
        <div
          className="flex-shrink-0 p-4"
          style={{ background: "transparent" }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div
                  className="flex items-center justify-center w-8 h-8 rounded-full"
                  style={{ backgroundColor: "#3B9827" }}
                >
                  <span className="text-sm font-medium text-white">
                    {safeGetDisplayName().charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="flex-1 ml-3">
                <p className="text-sm font-medium text-white">
                  {safeGetDisplayName()}
                </p>
                <p className="text-xs text-white">
                  {typeof user?.email === "string"
                    ? user.email
                    : "user@demo.com"}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="transition-colors text-primary-200 hover:text-white disabled:opacity-50"
              title="Logout"
            >
              {isLoggingOut ? (
                <div className="w-5 h-5 border-2 rounded-full animate-spin border-primary-200 border-t-white" />
              ) : (
                <LogOut className="w-5 h-5 text-dark-gray" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden lg:ml-0">
        {/* Top bar - mobile */}
        <div className="sticky top-0 z-40 bg-white border-b border-light-gray lg:hidden">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-secondary-500 hover:text-secondary-700"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center">
              <Building2 className="w-8 h-8 text-primary-600" />
              <span className="ml-2 text-lg font-semibold text-secondary-900">
                TBPP
              </span>
            </div>
            <div className="flex items-center gap-2">
              {["investor", "custodian"].includes(user?.role) && (
                <TopbarInbox />
              )}
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="relative p-2 rounded-full hover:bg-secondary-100 text-secondary-700 disabled:opacity-50"
                title="Logout"
                aria-label="Logout"
              >
                {isLoggingOut ? (
                  <div className="w-5 h-5 border-2 rounded-full animate-spin border-light-gray border-t-secondary-600" />
                ) : (
                  <LogOut className="w-6 h-6 text-dark-gray" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Top bar - desktop */}
        <div className="sticky top-0 z-30 hidden bg-white border-b border-light-gray lg:block">
          <div className="flex items-center justify-end gap-2 px-6 h-14">
            {["investor", "custodian"].includes(user?.role) && <TopbarInbox />}
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="relative p-2 rounded-full hover:bg-secondary-100 text-secondary-700 disabled:opacity-50"
              title="Logout"
              aria-label="Logout"
            >
              {isLoggingOut ? (
                <div className="w-5 h-5 border-2 rounded-full animate-spin border-secondary-300 border-t-secondary-600" />
              ) : (
                <LogOut className="w-6 h-6 text-dark-gray" />
              )}
            </button>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-6 overflow-x-hidden overflow-y-auto">
          {children}
        </main>
      </div>

      {/* Sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md p-6 mx-4 bg-white rounded-lg shadow-2xl">
            <div className="flex items-center mb-4">
              <LogOut className="w-6 h-6 mr-3 text-primary-600" />
              <h3 className="text-lg font-medium text-secondary-900">
                Confirm Logout
              </h3>
            </div>
            <p className="mb-6 text-sm text-secondary-600">
              Are you sure you want to log out? Unsaved changes may be lost.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              {/* Quick Logout removed */}
              <button
                onClick={confirmLogout}
                disabled={isLoggingOut}
                className="btn-primary"
              >
                {isLoggingOut ? "Logging out..." : "Confirm Logout"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating AI Assistant Button - Hidden */}
      <div className="fixed bottom-6 right-6 z-[9999] hidden">
        <button
          onClick={() => setShowAiChat(!showAiChat)}
          className="p-4 text-white transition-all duration-200 transform bg-blue-600 rounded-full shadow-lg hover:bg-blue-700 hover:scale-105"
          title="AI Support Assistant"
        >
          <Bot className="w-6 h-6" />
        </button>
      </div>

      {/* AI Chat Modal - Hidden */}
      {showAiChat && (
        <div className="fixed bottom-20 right-6 w-80 bg-white rounded-lg shadow-2xl border border-gray-200 z-[9999] hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center">
              <Bot className="w-5 h-5 mr-2 text-blue-600" />
              <h3 className="text-sm font-medium text-gray-900">
                AI Support Assistant
              </h3>
            </div>
            <button
              onClick={() => setShowAiChat(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-3 overflow-y-auto max-h-64">
            <div className="p-3 border-l-4 border-blue-400 rounded bg-blue-50">
              <p className="text-sm text-blue-700">
                👋 Hello! I'm your AI support assistant. I can help you with:
              </p>
              <ul className="mt-2 space-y-1 text-xs text-blue-600">
                <li>• System issues and troubleshooting</li>
                <li>• Process guidance and help</li>
                <li>• Creating support tickets</li>
                <li>• Feature explanations</li>
              </ul>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => {
                  if (user?.role === "super_admin") {
                    navigate("/admin/ai-agent");
                  } else {
                    alert(
                      "AI Agent: I'll create a support ticket for you. Please describe your issue"
                    );
                  }
                  setShowAiChat(false);
                }}
                className="w-full p-2 text-sm text-left text-gray-700 border rounded hover:bg-gray-50"
              >
                🎫 Create Support Ticket
              </button>

              <button
                onClick={() => {
                  alert(
                    "AI Agent: I'm here to help! What do you need assistance with today?"
                  );
                }}
                className="w-full p-2 text-sm text-left text-gray-700 border rounded hover:bg-gray-50"
              >
                💬 Quick Help
              </button>

              {user?.role === "super_admin" && (
                <button
                  onClick={() => {
                    navigate("/admin/ai-agent");
                    setShowAiChat(false);
                  }}
                  className="w-full p-2 text-sm text-left text-blue-700 border border-blue-200 rounded hover:bg-blue-50"
                >
                  🤖 Full AI Agent Dashboard
                </button>
              )}
            </div>
          </div>

          <div className="p-4 border-t border-gray-200">
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="Type your question"
                className="flex-1 text-sm border-gray-300 rounded focus:border-blue-500 focus:ring-blue-500"
                onKeyPress={(e) => {
                  if (e.key === "Enter" && e.target.value.trim()) {
                    alert(
                      `AI Agent: Thank you for your question: "${e.target.value}". I'm analyzing this and will provide assistance shortly`
                    );
                    e.target.value = "";
                  }
                }}
              />
              <button
                onClick={() =>
                  alert(
                    "AI Agent: Please type your question in the input field above"
                  )
                }
                className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                <span className="text-sm">Send</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;

// Lightweight inbox icon with recent announcements badge
const TopbarInbox = () => {
  const { user } = useAuth();
  const allowed = user?.role === "investor" || user?.role === "custodian";
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const key = `ann:lastSeen:${user?.role || "guest"}:${user?._id || "anon"}`;
  const [lastSeen, setLastSeen] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      return v ? parseInt(v, 10) : 0;
    } catch {
      return 0;
    }
  });

  useEffect(() => {
    // Sync lastSeen when user changes
    try {
      const v = localStorage.getItem(key);
      setLastSeen(v ? parseInt(v, 10) : 0);
    } catch {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!allowed) return;
    let mounted = true;
    const load = async () => {
      try {
        const resp = await announcementsAPI.getRecentAnnouncements(5);
        const arr =
          resp?.data?.data?.announcements || resp?.data?.announcements || [];
        if (!mounted) return;
        setItems(arr);
        const last = lastSeen || 0;
        const unread = arr.filter((a) => {
          const ts = new Date(
            a.publishedAt || a.createdAt || Date.now()
          ).getTime();
          return ts > last;
        }).length;
        setCount(unread);
      } catch (e) {
        // noop
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [allowed, lastSeen]);

  if (!allowed) return null;

  return (
    <div className="relative">
      <button
        className="relative p-2 rounded-full hover:bg-secondary-100"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) {
            const now = Date.now();
            try {
              localStorage.setItem(key, String(now));
            } catch {
              /* noop */
            }
            setLastSeen(now);
            setCount(0);
          }
        }}
        aria-label="Inbox"
        title="Announcements"
      >
        <Bell className="w-6 h-6 text-secondary-700" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 h-5 min-w-[20px] text-xs font-bold bg-error-500 text-white rounded-full">
            {count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 bg-white border rounded-md shadow-lg w-80 border-secondary-200">
          <div className="p-3 border-b">
            <div className="flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-secondary-800">
                Recent Announcements
              </span>
            </div>
          </div>
          <div className="overflow-y-auto max-h-80">
            {items.length === 0 ? (
              <div className="p-4 text-sm text-secondary-500">
                No announcements
              </div>
            ) : (
              items.map((a) => (
                <Link
                  key={a._id || a.id}
                  to={`${inferAnnouncementsPath()}/${a._id || a.id}`}
                  className="block p-3 border-b last:border-b-0 hover:bg-secondary-50"
                >
                  <div className="flex items-start justify-between">
                    <p className="text-sm font-medium text-secondary-900 line-clamp-2">
                      {a.title}
                    </p>
                  </div>
                  <p className="mt-1 text-xs text-secondary-600 line-clamp-2">
                    {a.content}
                  </p>
                  <p className="mt-1 text-2xs text-secondary-400">
                    {new Date(
                      a.publishedAt || a.createdAt || Date.now()
                    ).toLocaleString()}
                  </p>
                </Link>
              ))
            )}
          </div>
          <div className="p-2 text-right">
            <Link
              to={inferAnnouncementsPath()}
              className="text-xs text-primary-600 hover:underline"
            >
              View all
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

function inferAnnouncementsPath() {
  const path = window.location.pathname;
  if (path.startsWith("/issuer")) return "/issuer/announcements";
  if (path.startsWith("/custodian")) return "/custodian/announcements";
  if (path.startsWith("/admin")) return "/admin";
  return "/investor/announcements";
}
